<template>
	<div class="credit-form-container">
		<Form ref="creditForm" @submit="handleSubmit">
			<!-- Credit Type Selection -->
			<div class="col-md-8 mb-3" v-if="creditTypes && creditTypes.length > 0">
				<div class="w-100 d-flex flex-column flex-md-row justify-content-start align-items-center">
					<label class="form-label fw-normal col-12 col-md-6">Credit Type</label>
					<div class="col-12 col-md-6">
						<SelectField
							id="creditType"
							name="creditType"
							label="Select credit type"
							v-model="formData.creditType"
							:options="creditTypeOptions"
							rules="required"
						>
                        <option v-for="type in creditTypes" :key="type.name" :value="type.value">{{ type.name }}</option>
                        </SelectField>
					</div>
				</div>
			</div>

			<!-- Total Credit Amount -->
			<div class="col-md-8 mb-3">
				<div class="w-100 d-flex flex-column flex-md-row justify-content-start align-items-center">
					<label class="form-label fw-normal col-12 col-md-6">Total Credit Amount</label>
					<div class="col-12 col-md-6">
						<MoneyField 
							id="totalAmount"
							name="totalAmount"
							label="Input total amount"
							v-model="formData.totalAmount"
							currency="NGN"
							placeholder="₦0.00"
							locale="en-NG"
							@change="calculateRepayments"
							rules="required"
						/>
					</div>
				</div>
			</div>

			<!-- Repayment per Swap -->
			<div class="col-md-8 mb-3">
				<div class="w-100 d-flex flex-column flex-md-row justify-content-center align-items-center">
					<label class="form-label fw-normal col-12 col-md-6">Repayment per Swap</label>
					<div class="col-12 col-md-6">
						<MoneyField 
							id="repaymentPerSwap"
							name="repaymentPerSwap"
							label="Input repayment per swap"
							v-model="formData.repaymentPerSwap"
							currency="NGN"
							placeholder="₦0.00"
							locale="en-NG"
							@change="calculateRepayments"
							rules="required"
						/>
					</div>
				</div>
			</div>

			<!-- Number of Repayments -->
			<div class="col-md-8 mb-3">
				<div class="w-100 d-flex flex-column flex-md-row justify-content-center align-items-center">
					<label class="form-label fw-normal col-12 col-md-6">Number of Repayments</label>
					<div class="col-12 col-md-6">
						<InputField 
							id="numberOfRepayments"
							name="numberOfRepayments" 
							class="form-control" 
							v-model="formData.numberOfRepayments"
							label="Number of repayments"
							:disabled="true"
						/>
					</div>
				</div>
			</div>

			<!-- Legal Documents -->
			<div class="col-md-12 mt-4">
				<div class="border-bottom pb-2 mb-3">
					<h6 class="fw-bold mb-1">Required Legal Documents</h6>
					<p class="small text-muted mb-0">Please provide the necessary documents to approve this credit plan.</p>
				</div>
				<div class="row g-4 mb-4">
					<div class="col-12">
						<div class="mb-2">
							<label class="form-label fw-bold mb-0">Credit Agreement</label>
							<div class="small text-muted mb-2">Upload the signed credit agreement document.</div>
						</div>
						<div v-if="formData.credit_agreement" class="upload-done">
							<div class="d-flex align-items-center gap-2">
								<i class="fa fa-check-circle text-success"></i>
								<div>
									<div class="upload-done-name">Credit Agreement</div>
									<div class="upload-done-sub">Uploaded</div>
								</div>
							</div>
							<button type="button" class="btn btn-sm p-0 text-danger" @click="formData.credit_agreement = undefined"><i class="fa fa-times"></i></button>
						</div>
						<Dropzone v-else param="data" rules="required" name="credit_agreement" v-model="formData.credit_agreement" label="Credit Agreement" :url="route('upload.ajax')" class="dropzone-field" />
					</div>
				</div>
			</div>

			<!-- Action Buttons -->
			<div class="col-md-8">
				<div v-if="submitAttempted && !canSubmit" class="alert alert-danger py-2 px-3 mb-2 small" role="alert">
					<i class="fas fa-exclamation-circle me-1"></i>Please fill in all required fields before submitting.
				</div>
				<div class="d-flex gap-2 justify-content-end mt-3">
					<button
						type="button"
						class="btn btn-outline-danger px-4"
						@click="handleCancel"
						:disabled="loading"
					>
						Cancel
					</button>
					<button
						type="submit"
						class="btn bg-secondary text-light px-4"
						:disabled="loading"
						@click="onSubmitClick"
					>
						<span v-if="loading">
							<i class="fas fa-spinner fa-spin me-2"></i>Processing...
						</span>
						<span v-else>
							<i class="fas fa-plus me-2"></i>Add Credit Plan
						</span>
					</button>
				</div>
			</div>
		</Form>
	</div>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative, Emit, Watch } from 'vue-facing-decorator'
import Form from '@/vue/components/Form.vue'
import SelectField from '@/vue/components/SelectField.vue'
import InputField from '@/vue/components/InputField.vue'
import MoneyField from '@/vue/components/MoneyField.vue'
import Dropzone from '@/widget/Dropzone.vue'
import type { Options as Select2Options } from 'select2'

interface CreditFormData {
	creditType: string
	totalAmount: number
	repaymentPerSwap: number
	numberOfRepayments: number
	credit_agreement?: string
}

interface CreditType {
	value: string
	name: string
}

@Component({
	components: {
		Form,
		SelectField,
		InputField,
		MoneyField,
		Dropzone
	}
})
class CreditForm extends Vue {

    loading = false
	submitAttempted = false

	@Prop({ type: String, required: true })
	readonly url!: string

	@Prop({ type: Array, default: () => [] })
	readonly creditTypes!: CreditType[]

	@Prop({ type: [String, Number], required: true })
	readonly riderId!: string | number

	@Prop({ type: Boolean, default: false })
	readonly hasCreditAgreement!: boolean



	formData: CreditFormData = {
		creditType: '',
		totalAmount: 0,
		repaymentPerSwap: 0,
		numberOfRepayments: 0,
		credit_agreement: undefined
	}


	get creditTypeOptions(): Select2Options {
		return {
			placeholder: 'Select Credit Type',
			minimumResultsForSearch: Infinity,
			ajax:null
		}
	}

	get canSubmit(): boolean {
		const hasRequiredFields = this.formData.totalAmount > 0 && 
			this.formData.repaymentPerSwap > 0 && 
			this.formData.numberOfRepayments > 0

		// Check document fields
		const hasAgreement = this.hasCreditAgreement || !!this.formData.credit_agreement

		if (!hasAgreement) {
			return false
		}

		// If credit types are provided, also check if one is selected
		if (this.creditTypes && this.creditTypes.length > 0) {
			return hasRequiredFields && !!this.formData.creditType
		}

		return hasRequiredFields
	}

	get calculateRepayments() {
		if (this.formData.totalAmount > 0 && this.formData.repaymentPerSwap > 0) {
			this.formData.numberOfRepayments = Math.ceil(
				this.formData.totalAmount / this.formData.repaymentPerSwap
			)
		} else {
			this.formData.numberOfRepayments = 0
		}
        return this.formData.numberOfRepayments
	}

	onSubmitClick() {
		if (!this.canSubmit) {
			this.submitAttempted = true
		}
	}

	async handleSubmit(payload: any) {
		try {
			this.setLoading(true)

			const submissionData = {
				...payload,
				rider: this.riderId,
				totalAmount: payload.totalAmount * 100, // Convert to kobo
				repaymentPerSwap: payload.repaymentPerSwap * 100, // Convert to kobo
				creditType: this.formData.creditType || undefined,
				credit_agreement: this.formData.credit_agreement
			}

			const response = await this.$http.post(this.url, submissionData)

			this.onSuccess(response)
			this.resetForm()

		} catch (error) {
			this.onError(error)
		} finally {
			this.setLoading(false)
		}
	}

	@Emit('success')
	onSuccess(response: any) {
		return {
			message: response?.data?.message || 'Credit plan added successfully!',
			data: response?.data
		}
	}

	@Emit('error')
	onError(error: any) {
		return {
			message: error.response?.data?.message || 'Failed to add credit plan. Please try again.',
			error
		}
	}

	
	setLoading(state: boolean) {
		return this.loading = state
	}

	@Emit('cancel')
	handleCancel() {
		if (!this.loading) { 
            this.resetForm()
            return true
        }
        return false;
	}

	resetForm() {
		this.formData = {
			creditType: '',
			totalAmount: 0,
			repaymentPerSwap: 0,
			numberOfRepayments: 0,
			credit_agreement: undefined
		}
	}

	@Watch('canSubmit')
	onCanSubmitChange(val: boolean) {
		if (val) this.submitAttempted = false
	}

	mounted() {
		if (this.creditTypes && this.creditTypes.length === 1) {
			this.formData.creditType = this.creditTypes[0].value
		}
	}
}

export default toNative(CreditForm)
</script>

<style scoped>
.credit-form-container {
	width: 100%;
}

.form-label {
	margin-bottom: 0.5rem;
}

@media (max-width: 768px) {
	.col-md-6 {
		margin-bottom: 0.5rem;
	}
}

.upload-done {
	display: flex;
	align-items: center;
	justify-content: space-between;
	border-radius: 10px;
	padding: 0.6rem 0.875rem;
	border: 1px solid #e5e7eb;
	background: #f9fafb;
}

.upload-done-name {
	font-size: 0.8rem;
	font-weight: 600;
	color: #111827;
}

.upload-done-sub {
	font-size: 0.7rem;
	color: #6b7280;
}

.dropzone-field {
	border: 1.5px dashed #d1d5db;
	border-radius: 10px;
	background: #fafafa;
	transition: border-color 0.2s, background 0.2s;
	min-height: 80px;
}

.dropzone-field:hover {
	border-color: #3a7d52;
	background: #f0fdf4;
}
</style>